import type { ITourDto_Props } from './TourDto.if';
import type ISiteDto from './SiteDto.if';
import type ITourTypeDto from './TourTypeDto.if';
import type ITourSubTypeDto from './TourSubTypeDto.if';
import type ITourStatusDto from './TourStatusDto.if';
import type IStyleDto from './StyleDto.if';
import type ITourSupportedLanguageDto from './TourSupportedLanguageDto.if';
import type ITourTranslationDto from './TourTranslationDto.if';
import type ILanguageDto from './LanguageDto.if';
import type IFloorPlanDto from './FloorPlanDto.if';
import type { IModelSource } from '../interface/db';

export interface ITourDto_Linkables {
    site: (db: IModelSource) => Promise<ISiteDto>;
    tourType: (db: IModelSource) => Promise<ITourTypeDto>;
    tourSubType: (db: IModelSource) => Promise<ITourSubTypeDto>;
    style: (db: IModelSource) => Promise<IStyleDto>;
    tourStatus: (db: IModelSource) => Promise<ITourStatusDto>;
    tourSupportedLanguages: (db: IModelSource) => Promise<ITourSupportedLanguageDto[]>;
    translations: (db: IModelSource) => Promise<ITourTranslationDto[]>;
    translation: (
        db: IModelSource,
        language: ILanguageDto | number,
    ) => Promise<ITourTranslationDto>;
    floorplans: (db: IModelSource) => Promise<Array<IFloorPlanDto>>;
}

function _site(this: Partial<ITourDto_Props>, db: IModelSource): Promise<ISiteDto> {
    return this.siteId
        ? db.get('ISiteDto', this.siteId)
        : Promise.reject('Missing field tour.siteId');
}

function _tourType(this: Partial<ITourDto_Props>, db: IModelSource): Promise<ITourTypeDto> {
    return this.tourTypeId
        ? db.get('ITourTypeDto', this.tourTypeId)
        : Promise.reject('Missing field tour.tourTypeId');
}

function _tourSubType(this: Partial<ITourDto_Props>, db: IModelSource): Promise<ITourSubTypeDto> {
    return this.tourSubTypeId
        ? db.get('ITourSubTypeDto', this.tourSubTypeId)
        : Promise.reject('Missing field tour.tourSubTypeId');
}

function _style(this: Partial<ITourDto_Props>, db: IModelSource): Promise<IStyleDto> {
    return this.styleId
        ? db.get('IStyleDto', this.styleId)
        : Promise.reject('Missing field tour.styleId');
}

function _tourStatus(this: Partial<ITourDto_Props>, db: IModelSource): Promise<ITourStatusDto> {
    return this.tourStatusId
        ? db.get('ITourStatusDto', this.tourStatusId)
        : Promise.reject('Missing field tour.tourStatusId');
}

function _tourSupportedLanguages(
    this: Partial<ITourDto_Props>,
    db: IModelSource,
): Promise<Array<ITourSupportedLanguageDto>> {
    if (!this.id) {
        return Promise.reject('Missing field tour.id');
    }

    const self = this;
    return new Promise(function (resolve, reject) {
        db.all('ITourSupportedLanguageDto')
            .then(function (allTourSupportedLanguages) {
                const match = allTourSupportedLanguages.filter(x => x.tourId === self.id);
                resolve(match);
            })
            .catch(error => reject(error));
    });
}

function _translations(
    this: Partial<ITourDto_Props>,
    db: IModelSource,
): Promise<Array<ITourTranslationDto>> {
    if (!this.id) {
        return Promise.reject('Missing field tour.id');
    }

    const self = this;
    return new Promise(async function (resolve, reject) {
        try {
            /* TourDto  <-1-M=>  TourSupportedLanguageDto   <-1-1->   TourTranslationDto */
            const matchTSL = await _tourSupportedLanguages.call(self, db);
            const matchTSLIds = matchTSL.map(x => x.id);
            const allTranslations = await db.all('ITourTranslationDto');
            const matchTranslations = allTranslations.filter(x =>
                matchTSLIds.includes(x.tourSupportedLanguageId),
            );

            resolve(matchTranslations);
        } catch (error) {
            reject(error);
        }
    });
}

function _translation(
    this: Partial<ITourDto_Props>,
    db: IModelSource,
    language: ILanguageDto | number,
): Promise<ITourTranslationDto> {
    if (!this.id) {
        return Promise.reject('Missing field tour.id');
    }

    const self = this;

    const languageId: number =
        (language as ILanguageDto)?.id != undefined
            ? Number((language as ILanguageDto).id ?? -1)
            : Number(language);

    return new Promise(async function (resolve, reject) {
        try {
            const allSSL = await db.all('ISiteSupportedLanguageDto');
            const matchSSL = allSSL.filter(x => x.languageId === languageId);
            const matchSSLIds = matchSSL.map(x => x.id);

            const allTSL = await _tourSupportedLanguages.call(self, db);
            const matchTSL = allTSL.filter(
                x =>
                    matchSSLIds.includes(x.siteSupportedLanguageId) /* Workspace 1.0 */ ||
                    x.languageId === languageId /* Workspace 2.0 */,
            );
            const matchTSLIds = matchTSL.map(x => x.id);

            const tourTranslations = await _translations.call(self, db);

            let matchTranslations = tourTranslations.filter(x =>
                matchTSLIds.includes(x.tourSupportedLanguageId),
            );

            /* Workspace 2.0 */
            if (matchTranslations.length <= 0 && matchTSLIds.length > 0) {
                matchTranslations = [await db.get('ITourTranslationDto', matchTSLIds[0])];
            }

            matchTranslations.length > 0
                ? resolve(matchTranslations[0])
                : reject(
                      `Unable to find translation for languageId:${languageId}, tourId:${self.id}`,
                  );
        } catch (error) {
            reject(error);
        }
    });
}

function _floorplans(
    this: Partial<ITourDto_Props>,
    db: IModelSource,
): Promise<Array<IFloorPlanDto>> {
    if (!this.id) {
        return Promise.reject('Missing field tour.id');
    }

    const thisId = this.id;

    return new Promise(async function (resolve, reject) {
        try {
            const allFloorPlanTours = await db.all('IFloorPlanTourDto');
            const matchFPTours = allFloorPlanTours.filter(fpTour => fpTour.tourId === thisId);
            const matchFloorPlans = await Promise.all(
                matchFPTours.map(fpTour => db.get('IFloorPlanDto', fpTour.floorPlanId)),
            );

            resolve(matchFloorPlans);
        } catch (error) {
            reject(error);
        }
    });
}

/** 
 * @description all custom properties for
   TourDto & methods to link against other entities
*/
function linkables(model: Partial<ITourDto_Props>): ITourDto_Linkables {
    return {
        site: _site.bind(model),
        tourType: _tourType.bind(model),
        tourSubType: _tourSubType.bind(model),
        style: _style.bind(model),
        tourStatus: _tourStatus.bind(model),
        tourSupportedLanguages: _tourSupportedLanguages.bind(model),
        translations: _translations.bind(model),
        translation: _translation.bind(model),
        floorplans: _floorplans.bind(model),
    };
}

export default linkables;
